/* eslint-disable no-console */
import { useRef } from "react"
import { z } from "zod"

import {
  isInboundCall,
  isOutboundCall,
  isTask,
} from "@/components/core/ConnectCCP/contact_helpers"
import { UndocumentedAgentStateType } from "@/helpers/atoms"
import { getContactPhoneNumber } from "@/helpers/phoneNumber"

import { useAgentState } from "."
import { AgentState } from "./state-types"
import { extractTaskProps } from "./task-data"

export const pseudoACWStatus = [
  "AgentHungUp", // an error triggered just after an agent calls a student
  "FailedConnectAgent",
  "FailedConnectCustomer",
  "MissedCallCustomer",
  "BadAddressAgent",
  "BadAddressCustomer",
  "LineEngagedAgent",
  "LineEngagedCustomer",
]

export const CUSTOM_PSEUDO_ACW_STATUS = "PseudoACW" // custom state we created from AWS Connect console to ensure agents don't receive calls

// All possible agent states triggered by agent.onStateChange()
const connectAgentStatusSchema = z.enum([
  "AfterCallWork",
  "Available",
  "Busy", // in a call
  "CallingCustomer", // making an outbound call
  "Init",
  "MissedCallAgent", // agent did not pick up the phone
  "Offline",
  "PendingBusy", // a student is calling
  CUSTOM_PSEUDO_ACW_STATUS,
  ...pseudoACWStatus,
])

export type ConnectAgentStatus = z.infer<typeof connectAgentStatusSchema>

/** Main hook to link the AWS Connect Agent to the state machine  */
export function useSetupConnectListeners() {
  const { agentRef, send } = useAgentState()
  const contactRegisteredRef = useRef(false)

  const onAuthenticated = () => {
    connect.agent((agent) => {
      agentRef.current = agent

      send({
        type: "AGENT_LOADED",
        agentData: { queueNames: getQueueNames(agent) },
      })

      agent.onStateChange(({ newState, oldState }) => {
        console.log(">> agent.onStateChange", oldState, "=>", newState)

        const result = connectAgentStatusSchema.safeParse(newState)
        if (!result.success) {
          console.error(">> Invalid agent status", newState)

          return
        }

        send({ type: "CHANGE_CONNECT_AGENT_STATUS", status: result.data })
      })
    })

    if (!contactRegisteredRef.current) {
      contactRegisteredRef.current = true

      connect.contact((contact) => {
        console.log(">> Contact", contact.getType(), contact.getStatus().type)

        if (isOutboundCall(contact)) {
          sendVoiceContactId(contact)
        } else {
          sendConnectContactEvent(contact)
        }

        contact.onError((contact) => {
          console.error(">> Contact Error", contact.getStatus().type)
          send({
            type: "CONTACT_ERROR",
            status: contact.getStatus().type,
            isInboundCall: isInboundCall(contact),
            isTask: isTask(contact),
          })
        })
      })
    }

    function sendVoiceContactId(contact: connect.Contact) {
      const contactId = contact.getContactId()

      send({ type: "NEW_OUTBOUND_VOICE_CONTACT_ID", contactId })
    }

    function sendConnectContactEvent(contact: connect.Contact) {
      const data = getEventData(contact)

      send({ type: "NEW_TASK", task: data })
    }

    function getEventData(contact: connect.Contact) {
      const agent = agentRef.current
      const contactId = contact.getContactId()
      const isContactTask = isTask(contact)
      const data = agent ? extractTaskProps(agent) : null

      const campaignId =
        data?.campaignId || (agent ? getCampaignId(agent) : undefined)
      const caseId = data?.caseId || undefined
      const phoneNumber =
        data?.phoneNumber || (agent ? getContactPhoneNumber(agent) : undefined)
      const studentId = data?.studentId || undefined

      const taskMeta: AgentState["taskMeta"] = {
        callDirection: isInboundCall(contact) ? "inbound" : "outbound",
        caseId,
        origin: "connect",
        phoneNumber: phoneNumber || undefined,
        campaignId: campaignId || undefined,
        studentId,
        contactId,
        taskId: isContactTask ? contactId : null,
        voiceId: isInboundCall(contact) ? contactId : null,
        connectContactStatus: isContactTask
          ? contact.getStatus().type
          : undefined,
      }

      return taskMeta
    }
  }

  return { onAuthenticated }
}

function getCampaignId(agent: connect.Agent) {
  return agent?.getContacts()[0]?.getQueue().name
}

function getQueueNames(agent: connect.Agent) {
  return (
    agent
      ?.getRoutingProfile()
      .queues.map((queue) => queue.name)
      .filter(Boolean) || [] // remove `null` values
  )
}

export function isContactOnHold(agent: connect.Agent) {
  const contacts = agent.getContacts(connect.ContactType.VOICE)

  if (!contacts.length) {
    return false
  }

  const activeConnection = contacts[0]?.getInitialConnection()

  return activeConnection?.isOnHold()
}

export function useGetConnectAgentStatus():
  | connect.AgentStateType
  | UndocumentedAgentStateType {
  const { agentRef } = useAgentState()

  return agentRef.current?.getStatus().type || connect.AgentStateType.INIT
}
